import { Slot } from '@radix-ui/react-slot';
import { cn } from '@repo/lib/cn';
import { createContext, use, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';

import type { FullscreenContextValue, FullscreenRootProps, FullscreenTargetProps, FullscreenTriggerProps } from '.';

const FullscreenContext = createContext<FullscreenContextValue | null>(null);

export function useFullscreen() {
  const ctx = use(FullscreenContext);
  if (!ctx) throw new Error('useFullscreen must be used within <Fullscreen.Root>');
  return ctx;
}

function getFullscreenElement(): Element | null {
  const doc = document as Document & { webkitFullscreenElement?: Element };
  return document.fullscreenElement || doc.webkitFullscreenElement || null;
}

function requestFullscreen(el: HTMLElement): Promise<void> | void {
  const anyEl = el as HTMLElement & { webkitRequestFullscreen?: () => void };
  if (el.requestFullscreen) return el.requestFullscreen();
  if (anyEl.webkitRequestFullscreen) return anyEl.webkitRequestFullscreen();
}

function exitFullscreen(): Promise<void> | void {
  const doc = document as Document & { webkitExitFullscreen?: () => void };
  if (document.exitFullscreen) return document.exitFullscreen();
  if (doc.webkitExitFullscreen) return doc.webkitExitFullscreen();
}

export function FullscreenRoot(fullscreenRootProps: FullscreenRootProps) {
  const { children } = fullscreenRootProps;
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const targetEl = useRef<HTMLDivElement | null>(null);
  const handleChange = useCallback(() => {
    const current = getFullscreenElement();
    setIsFullscreen(!!current && current === targetEl.current);
  }, [targetEl]);

  const enter = useCallback(async () => {
    if (!targetEl.current) return;
    await requestFullscreen(targetEl.current);
  }, [targetEl]);

  const exit = useCallback(async () => {
    await exitFullscreen();
  }, []);

  const toggle = useCallback(() => {
    if (isFullscreen) void exit();
    else void enter();
  }, [isFullscreen, enter, exit]);

  useEffect(() => {
    const onChange = () => handleChange();
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, [handleChange]);

  const value = useMemo<FullscreenContextValue>(
    () => ({
      isFullscreen,
      enter,
      exit,
      toggle,
      targetEl,
    }),
    [isFullscreen, enter, exit, toggle, targetEl]
  );

  return <FullscreenContext value={value}>{children}</FullscreenContext>;
}

export function FullscreenTarget(fullscreenTargetProps: FullscreenTargetProps) {
  const { asChild, className, fullscreenClassName, children, ref, ...props } = fullscreenTargetProps;

  const Comp = asChild ? Slot : 'div';
  const { isFullscreen, targetEl } = useFullscreen();

  useImperativeHandle(ref, () => targetEl.current!, []);

  return (
    <Comp ref={targetEl} className={cn(className, isFullscreen && fullscreenClassName)} {...props}>
      {children}
    </Comp>
  );
}

export function FullscreenTrigger(fullscreenTriggerProps: FullscreenTriggerProps) {
  const { asChild, mode = 'toggle', onClick, children, ...props } = fullscreenTriggerProps;
  const { toggle, enter, exit, isFullscreen } = useFullscreen();
  const Comp = asChild ? Slot : 'button';

  const handleOnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(e);
    if (e.defaultPrevented) {
      return;
    }
    if (mode === 'toggle') {
      toggle();
    } else if (mode === 'enter') {
      enter();
    } else {
      exit();
    }
  };

  return (
    <Comp type="button" {...props} onClick={handleOnClick}>
      {typeof children === 'function' ? children(isFullscreen) : children}
    </Comp>
  );
}
